import { savePDF } from '@progress/kendo-vue-pdf';

export type PDFExportOptions = {
    /**
     * Specifies the name of the exported PDF file.
     * @default "export.pdf"
     */
    fileName?: string;
    /**
     * A flag that indicates if the page will be in a landscape orientation.
     * By default, the page is in a portrait orientation.
     *
     * @default false
     */
    landscape?: boolean;
    /**
     * Specifies the margins of the page.
     *
     * The supported units are:
     * * `"mm"`
     * * `"cm"`
     * * `"in"`
     * * `"pt"` (default).
     *
     * > Numbers are considered to be points (`"pt"`).
     */
    margin?: string | number | PageMargin;
    /**
     * Specifies the paper size of the PDF document. Defaults to `"auto"` which means that the paper size
     * is determined by the content.
     * The size of the content in pixels matches the size of the output in points (1 pixel = 1/72 inch).
     *
     * If set, the content will be split across multiple pages.
     * This enables the `repeatHeaders` and `scale` options, and allows you to specify a template.
     *
     * The supported values are:
     * * A predefined size. The supported paper sizes are: `A0-A10`, `B0-B10`, `C0-C10`,
     * `Executive`, `Folio`, `Legal`, `Letter`, `Tabloid`.
     * * An array of two numbers which specify the width and height in points (1pt = 1/72in).
     * * An array of two strings which specify the width and height in units.
     * The supported units are `"mm"`, `"cm"`, `"in"`, and `"pt"`.
     */
    paperSize: PaperSize;
    /**
     * A scale factor.
     * The text size on the screen might be too big for printing.
     * To scale down the output in PDF, use this option.
     *
     * @default 1
     */
    scale?: number;
    /**
        * An optional CSS selector that specifies the elements which cause the page breaks.
        */
    forcePageBreak?: string;
};

export type PaperSize =
    string | [number | string, number | string] | "auto" |
    "A0" | "A1" | "A2" | "A3" | "A4" | "A5" |
    "A6" | "A7" | "A8" | "A9" | "A10" |
    "B0" | "B1" | "B2" | "B3" | "B4" | "B5" |
    "B6" | "B7" | "B8" | "B9" | "B10" |
    "C0" | "C1" | "C2" | "C3" | "C4" | "C5" |
    "C6" | "C7" | "C8" | "C9" | "C10" |
    "Executive" | "Folio" | "Legal" | "Letter" | "Tabloid";

export type PageMargin = {
    /**
     * The bottom margin.
     *
     * > Numbers are treated as points (`"pt"`).
     *
     * The supported units are:
     * * `"mm"`
     * * `"cm"`
     * * `"in"`
     * * `"pt"` (default).
     */
    left?: number | string;
    /**
     * The top margin.
     *
     * > Numbers are treated as points (`"pt"`).
     *
     * The supported units are:
     * * `"mm"`
     * * `"cm"`
     * * `"in"`
     * * `"pt"` (default).
     */
    top?: number | string;
    /**
     * The right margin.
     *
     * > Numbers are treated as points (`"pt"`).
     *
     * The supported units are:
     * * `"mm"`
     * * `"cm"`
     * * `"in"`
     * * `"pt"` (default).
     */
    right?: number | string;
    /**
     * The bottom margin.
     *
     * > Numbers are treated as points (`"pt"`).
     *
     * The supported units are:
     * * `"mm"`
     * * `"cm"`
     * * `"in"`
     * * `"pt"` (default).
     */
    bottom?: number | string;
};

export const exportToPDF = (element: HTMLElement, options: PDFExportOptions) => {
    savePDF(element, {
        fileName: options.fileName || 'export.pdf',
        landscape: options.landscape || false,
        margin: options.margin || { left: 0, top: 0, right: 0, bottom: 0 },
        paperSize: options.paperSize || 'auto',
        scale: options.scale || 1,
        forcePageBreak: options.forcePageBreak || undefined
    });
};